import React from 'react';

export enum StackDirection {
  Vertical = 'vertical',
  Horizontal = 'horizontal',
}

export enum StackSpacing {
  XS = 'xs',
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
  XL = 'xl',
  XXL = 'xxl',
}

export enum StackAlign {
  Start = 'start',
  Center = 'center',
  End = 'end',
  Stretch = 'stretch',
}

export enum StackJustify {
  Start = 'start',
  Center = 'center',
  End = 'end',
  Between = 'between',
  Around = 'around',
  Evenly = 'evenly',
}

interface StackProps {
  children: React.ReactNode;
  direction?: StackDirection;
  spacing?: StackSpacing;
  align?: StackAlign;
  justify?: StackJustify;
  className?: string;
}

const spacingClasses = {
  vertical: {
    xs: 'space-y-1',
    sm: 'space-y-2',
    md: 'space-y-2',
    lg: 'space-y-2',
    xl: 'space-y-2',
    xxl: 'space-y-2',
  },
  horizontal: {
    xs: 'space-x-1',
    sm: 'space-x-2',
    md: 'space-x-4',
    lg: 'space-x-6',
    xl: 'space-x-8',
    xxl: 'space-x-12',
  },
};

const alignClasses = {
  vertical: {
    start: 'items-start',
    center: 'items-center',
    end: 'items-end',
    stretch: 'items-stretch',
  },
  horizontal: {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    stretch: 'justify-stretch',
  },
};

const justifyClasses = {
  vertical: {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    between: 'justify-between',
    around: 'justify-around',
    evenly: 'justify-evenly',
  },
  horizontal: {
    start: 'items-start',
    center: 'items-center',
    end: 'items-end',
    between: 'items-between',
    around: 'items-around',
    evenly: 'items-evenly',
  },
};

export const Stack: React.FC<StackProps> = ({
  children,
  direction = StackDirection.Vertical,
  spacing = StackSpacing.MD,
  align = StackAlign.Stretch,
  justify = StackJustify.Start,
  className = '',
}) => {
  const directionClass = direction === StackDirection.Vertical ? 'flex flex-col' : 'flex flex-row';
  const spacingClass = spacingClasses[direction][spacing];
  const alignClass = alignClasses[direction][align];
  const justifyClass = justifyClasses[direction][justify];

  const classes = [directionClass, spacingClass, alignClass, justifyClass, className].filter(Boolean).join(' ');

  return <div className={classes}>{children}</div>;
};
